/** Wie ein Dokument in der Vorschau dargestellt wird.
 *
 *  Entschieden wird nach der **Dateiendung**, nicht nach dem MIME-Typ der
 *  Datenbank: der ist bei Altbestand nicht verlässlich (die
 *  Handelsregister-Ablage hat bis QS #24 jede Datei als application/pdf
 *  deklariert, auch die XML-Inhaltsdateien).
 */

export type DocumentPreviewKind = 'pdf' | 'image' | 'text' | 'none';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];

/** Textformate, die sich sinnvoll im Klartext zeigen lassen. `xml` ist der
 *  Anlass: die strukturierte Inhaltsdatei (SI) des Handelsregisters landete
 *  bisher unter „Keine Vorschau". */
const TEXT_EXTENSIONS = ['xml', 'txt', 'json', 'csv'];

export const fileExtension = (fileName?: string | null): string => {
  if (typeof fileName !== 'string') return '';
  const parts = fileName.toLowerCase().split('.');
  return parts.length > 1 ? (parts.pop() ?? '') : '';
};

/** Zuordnung der MIMEType-Ids aus der Datenbank. Die Tabelle fuehrt PDF, JPEG
 *  und PNG doppelt (1-3 und 4-6); 7 ist XML. */
const KIND_BY_MIME_TYPE_ID: Record<number, DocumentPreviewKind> = {
  1: 'pdf',
  4: 'pdf',
  2: 'image',
  5: 'image',
  3: 'image',
  6: 'image',
  7: 'text',
};

/** Art der Vorschau.
 *
 *  Die Dateiendung hat Vorrang: im Altbestand ist der MIME-Typ nicht
 *  verlaesslich, weil die Handelsregister-Ablage bis QS #24 jede Datei als
 *  application/pdf deklariert hat — eine `.xml` mit MIME "PDF" soll trotzdem
 *  als Text erscheinen.
 *
 *  Fehlt eine brauchbare Endung, entscheidet `idMIMEType`. Das ist der Fall,
 *  der die Anzeige im Ergebnis-Dialog der Nachverfolgung betraf: dort wird ein
 *  Platzhalter-Dokument mit dem Namen `Dokument 18611` gebaut — ohne Endung,
 *  also nach alter Logik weder PDF noch Bild und damit "Keine Vorschau".
 */
export const documentPreviewKind = (
  fileName?: string | null,
  idMIMEType?: number | null
): DocumentPreviewKind => {
  const extension = fileExtension(fileName);
  if (extension === 'pdf') return 'pdf';
  // SVG vor den Textformaten: es ist XML, wird aber als Bild dargestellt.
  if (IMAGE_EXTENSIONS.includes(extension)) return 'image';
  if (TEXT_EXTENSIONS.includes(extension)) return 'text';

  if (typeof idMIMEType === 'number') {
    return KIND_BY_MIME_TYPE_ID[idMIMEType] ?? 'none';
  }
  return 'none';
};

/** Vorschaugrenze für Textdateien. Eine SI-Datei ist wenige Kilobyte groß;
 *  alles darüber will man nicht in einer Karte lesen. */
const TEXT_PREVIEW_LIMIT = 20000;

/** Base64-Inhalt als Text. `null`, wenn nichts oder nichts Lesbares vorliegt.
 *  Sehr lange Inhalte werden gekürzt, mit sichtbarem Hinweis — stillschweigend
 *  abzuschneiden wäre schlimmer als es zu sagen. */
export const decodeTextDocument = (
  base64?: string | null,
  limit: number = TEXT_PREVIEW_LIMIT
): string | null => {
  if (!base64) return null;

  try {
    // Bewusst ohne TextDecoder: der fehlt in der jsdom-Testumgebung, und diese
    // Umschreibung auf Prozent-Notation dekodiert UTF-8 zuverlaessig ueberall.
    const binary = atob(base64);
    const text = decodeURIComponent(
      Array.from(binary)
        .map(char => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join('')
    );
    if (!text) return null;

    if (text.length > limit) {
      return `${text.slice(0, limit)}\n\n… gekürzt, Datei zum vollständigen Lesen herunterladen.`;
    }
    return text;
  } catch {
    return null;
  }
};
